import { Note } from '@teetsh/app/src/containers/CarnetsDeNotes/types/carnetsDeNotes';
import { NotationSystem } from '@teetsh/app/src/types/notationSystem';
import { v4 as uuid } from 'uuid';

interface EvaluationStructure {
  referentialIds: (string | undefined)[];
  isMultiSkill: boolean;
}

/**
 * Analyzes existing notes to determine the evaluation structure
 * (multi-skill vs single-skill, and which referential items are included)
 */
export function getEvaluationStructure(
  existingNotes: Note[],
): EvaluationStructure {
  // Group notes by student to understand the structure
  const notesByEleve = existingNotes.reduce((acc, note) => {
    if (!acc[note.eleveId]) {
      acc[note.eleveId] = [];
    }
    acc[note.eleveId].push(note);
    return acc;
  }, {} as Record<string, Note[]>);

  // Find a student who has notes to use as a template
  const templateStudentNotes = Object.values(notesByEleve).find(
    studentNotes => studentNotes.length > 0,
  );

  // Get referential IDs from the template student (this preserves the order and structure)
  const referentialIds = templateStudentNotes
    ? templateStudentNotes.map(note => note.referentialItemId)
    : [];

  // Check if this is a multi-skill evaluation
  const isMultiSkill =
    referentialIds.length > 1 ||
    (referentialIds.length === 1 && referentialIds[0] !== undefined);

  return { referentialIds, isMultiSkill };
}

/**
 * Creates notes for a student based on the evaluation structure
 */
export function createNotesForStudent(
  eleveId: string,
  evaluationId: string,
  evaluationStructure: EvaluationStructure,
  notationSystem?: NotationSystem,
): Note[] {
  const { referentialIds, isMultiSkill } = evaluationStructure;

  // For multi-skill evaluations, create a note for each referential item
  if (isMultiSkill && referentialIds.length > 0) {
    return referentialIds.map(refId => ({
      id: uuid(),
      eleveId,
      evaluationId,
      referentialItemId: refId,
      notationSystemId: notationSystem?.id,
      notationSystemMin: notationSystem?.minValue,
      notationSystemMax: notationSystem?.maxValue,
    }));
  }

  // For single-skill evaluations, create a single note with undefined referentialItemId
  return [
    {
      id: uuid(),
      eleveId,
      evaluationId,
      referentialItemId: undefined,
      notationSystemMin: notationSystem?.minValue,
      notationSystemMax: notationSystem?.maxValue,
    },
  ];
}
